import {
  BaseQueryFn,
  createApi,
  defaultSerializeQueryArgs,
} from '@reduxjs/toolkit/query/react';

/**
 * Usage de endponts inmemory ou axios, selon la variable d'environnement local ou !== local
 */
import { inMemoryBaseQuery } from '../in-memory-config/in-memory-base-query';
import { axiosBaseQuery } from '../axios-config/axios-base-query';

/**
 * Chargement des slices rtk query en fonction du front end : CRM | Espace client
 */
import { useCustomerAreaConfiguration } from './customer-area-configuration';
import { useCrmConfiguration } from './crm-configuration';

/**
 * Typage de chaque slice pour déclarer ses endpoints et pouvoir destructurer apiSlice en fin de fichier
 * Rend accessible les hooks à travers les applications
 */
import { AlertSliceType } from '../module/alert/alert-slice.api';
import { CatalogueSliceType } from '../module/catalogue/catalogue-slice.api';
import { ChatSliceType } from '../module/chat/chat-slice.api';
import { CommentSliceType } from '../module/comment/comment-slice.api';
import { CustomerChatSliceType } from '../module/customer-chat/customer-chat-slice.api';
import { CustomerConfigurationSliceType } from '../module/customer-configuration/customer-configuration-slice.api';
import { CustomerDocumentSliceType } from '../module/customer-document/customer-document-slice.api';
import { CustomerProjectSliceType } from '../module/customer-project/customer-project-slice.api';
import { DocumentSliceType } from '../module/document/document-slice.api';
import { MainSliceType } from '../module/main/main-slice.api';
import { MessageSliceType } from '../module/message/message-slice.api';
import { MondaySliceType } from '../module/monday/monday-slice.api';
import { MonitoringSliceType } from '../module/monitoring/monitoring-slice.api';
import { NotificationSliceType } from '../module/notification/notification-slice.api';
import { ProjectSliceType } from '../module/project/project-slice.api';
import { QuoteSliceType } from '../module/quote/quote-slice.api';
import { TaskSliceType } from '../module/task/task-slice.api';
import { UpsellSliceType } from '../module/upsell/upsell-slice.api';
import { UserSliceType } from '../module/user/user-slice.api';
import { FilesSliceType } from '../module/files/files-slice.api';
import { ConfigurationSliceType } from '../module/configuration/configuration-slice.api';
import { RibSliceType } from '../module/rib/rib-slice.api';

/**
 * Typage manuel
 */
interface ApiSlice
  extends AlertSliceType,
    CatalogueSliceType,
    ChatSliceType,
    CommentSliceType,
    CustomerChatSliceType,
    CustomerConfigurationSliceType,
    CustomerDocumentSliceType,
    CustomerProjectSliceType,
    DocumentSliceType,
    MainSliceType,
    MessageSliceType,
    MondaySliceType,
    MonitoringSliceType,
    NotificationSliceType,
    ConfigurationSliceType,
    ProjectSliceType,
    QuoteSliceType,
    FilesSliceType,
    TaskSliceType,
    UpsellSliceType,
    UserSliceType,
    RibSliceType {}

class RTK {
  private apiSlice: any = null;

  private initApiSlice(customeBaseQuery: BaseQueryFn) {
    this.apiSlice = createApi({
      reducerPath: 'api',
      baseQuery: customeBaseQuery,
      // keepUnusedDataFor: 100,
      serializeQueryArgs: (args) => {
        if (args.queryArgs?.useCaseName !== undefined) {
          return args.queryArgs.useCaseName;
        } else {
          return defaultSerializeQueryArgs(args);
        }
      },
      endpoints: (builder) => ({}),
    });
  }

  /**
   * TODODEV : faire accepter import.meta à Typescrip puis retirer les @ts-ignore ci-dessous
   */

  public configureApiSlice() {
    // Main configuration
    // @ts-ignore
    if (import.meta.env['VITE_ENVIRONNEMENT'] !== 'local') {
      // Axios Base Query for HTTP Call
      this.initApiSlice(
        axiosBaseQuery({
          // @ts-ignore
          baseUrl: import.meta.env['VITE_API_URL'],
        })
      );
    } else {
      // In Memory Base Query for local mock data
      this.initApiSlice(inMemoryBaseQuery());
    }

    // Configure by app
    if (
      // @ts-ignore
      import.meta.env['VITE_CURRENT_FRONT_URL'] ===
      // @ts-ignore
      import.meta.env['VITE_CRM_URL']
    ) {
      useCrmConfiguration(this.apiSlice);
    } else if (
      // @ts-ignore
      import.meta.env['VITE_CURRENT_FRONT_URL'] ===
      // @ts-ignore
      import.meta.env['VITE_CUSTOMER_AREA_URL']
    ) {
      useCustomerAreaConfiguration(this.apiSlice);
    }
  }

  public getApiSlice() {
    return this.apiSlice as ApiSlice;
  }
}

/**
 * Factory d'un singleton de rtk query, pour disposer de la variable apiSlice dans ce fichier une fois configuré, autant que pour l'exporter
 */
const ApiSliceFactory = () => {
  let instance: RTK;
  return {
    getInstance: function () {
      if (instance == null) {
        instance = new RTK();
        instance.configureApiSlice();
      }
      return instance.getApiSlice();
    },
  };
};

export const apiSlice = ApiSliceFactory().getInstance();

export default apiSlice;

export const {
  // Alert
  useGetAllAlertsQuery,
  useLazyGetAllAlertsQuery,
  usePatchAlertAsSeenMutation,
  // Catalogue
  useGetCatalogueQuery,
  useGetAllModelsQuery,
  useGetModelbyIdQuery,
  useGetAllFinishesQuery,
  useGetFinishByIdQuery,
  useGetAllEquipmentsQuery,
  useGetEquipmentByIdQuery,
  useGetAllStudiosQuery,
  useGetStudioByIdQuery,
  useGetAllKitsQuery,
  useGetKitByIdQuery,
  useGetAllOptionsQuery,
  useGetOptionByIdQuery,
  useSyncStudiosMutation,
  useSyncModelsFinishsEquipementsMutation,
  useSyncOptionsMutation,
  useGetBimCatalogueQuery,
  useGetBimNodeCatalogueQuery,
  useAddElementToBimCatalogueMutation,
  usePostFileToBimCatalogueMutation,
  useGetAllDefaultOptionFinishEquipmentQuery,
  usePostDefaultOptionFinishEquipmentMutation,
  usePatchDefaultOptionFinishEquipmentMutation,
  useDeleteDefaultOptionFinishEquipmentMutation,
  // Chat
  useGetChatByProjectQuery,
  useLazyGetChatByProjectQuery,
  usePostChatMessageMutation,
  useDeleteChatMessageMutation,
  // Comment
  useGetCommentByProjectAndSlugQuery,
  useLazyGetCommentByProjectAndSlugQuery,
  usePatchCommentMutation,
  // Configuration
  useGetConfigurationsByProjectQuery,
  usePatchConfigurationsMutation,
  useGetConfigurationDifferenceMutation,
  useGetConfigurationFormatToDocMutation,
  useGetTempConfigurationsPriceQuery,
  useLazyGetTempConfigurationsPriceQuery,
  // CustomerChat
  useGetCustomerChatQuery,
  useLazyGetCustomerChatQuery,
  usePostCustomerChatMessageMutation,
  useDeleteCustomerChatMessageMutation,
  // CustomerConfiguration
  usePatchCustomerConfigurationsMutation,
  // CustomerDocument
  useGetCustomerDocumentsByProjectQuery,
  useLazyGetCustomerDocumentsByProjectQuery,
  usePostCustomerSingleDocumentMutation,
  useGetCustomerDocumentByIdQuery,
  useGetCustomerDocumentByProjectAndSlugQuery,
  useGetCustomerDocumentCategoriesQuery,
  useLazyGetCustomerDocumentCategoriesQuery,
  useGetCustomerStandardizedDocumentsQuery,
  useLazyGetCustomerStandardizedDocumentsQuery,
  usePatchCustomerSignDocumentMutation,
  usePatchCustomerDocumentMutation,
  useDeleteCustomerDocumentMutation,
  // CustomerFile

  // CustomerProject
  useGetAllCustomerProjectsQuery,
  useGetCustomerProjectByTokenQuery,
  useGetCustomerTasksByProjectQuery,
  usePostCustomerAskSomethingMutation,
  useGetCustomerNotificationsByProjectQuery,
  usePatchCustomerNotificationAsSeenMutation,
  // Document
  useGetDocumentsByProjectQuery,
  useLazyGetDocumentsByProjectQuery,
  useGetDocumentByIdQuery,
  useLazyGetDocumentByIdQuery,
  useGetDocumentCategoriesQuery,
  useLazyGetDocumentCategoriesQuery,
  useGetStandardizeDocumentsQuery,
  useLazyGetStandardizeDocumentsQuery,
  usePostDocumentMutation,
  usePostSingleDocumentStandardizedMutation,
  useDeleteDocumentMutation,
  usePatchDocumentMutation,
  usePatchStandardizedDocumentMutation,
  usePatchValidateDocumentMutation,
  usePatchRefuseDocumentMutation,
  usePatchArchiveDocumentMutation,
  usePatchSignatureExistingDocMutation,
  usePatchSignatureGeneratingDocMutation,
  useSendDocumentByMailQuery,
  useGenerateDocumentMutation,
  // Files
  useGetDocumentBlobByIdMutation,
  useDownloadMutation,
  useGetDocumentBlobByProjectAndSlugMutation,
  useDownloadDocumentBySlugMutation,
  useGetThumbnailMutation,
  useGenerateFileMutation,
  useGetTempAmendementFileMutation,
  useGenerateQuoteDocumentAndGetFileMutation,
  useGetQuoteFileFromMainConfigMutation,
  useLazyGetQuoteFileFromMainConfigMutation,
  useDownloadIfcMutation,

  // Main
  useGetLatestPlanDePrincipeDataQuery,
  usePostErrorMessageMutation,
  // Message
  useGetMessagesByProjectQuery,
  usePostMessageMutation,
  usePatchMessageMutation,
  useDeleteMessageMutation,
  // Monday
  useGetAllMondayBoardQuery,
  useDeleteMondayBoardMutation,
  usePostMondayBoardMutation,
  useUpdateMondayBoardMutation,
  // Monitoring
  usePingMutation,
  useGetAllPingByRoleAndUsernameQuery,
  // Notification
  useGetAllNotificationsByProjectQuery,
  useGetAllNotificationTypesQuery,
  usePostNotificationMutation,
  useDeleteNotificationMutation,
  usePatchNotificationMutation,
  // Project
  useGetProjectByIdQuery,
  useLazyGetProjectByIdQuery,
  useGetProjectsByUserQuery,
  useLazyGetProjectsByUserQuery,
  useGetProjectByTokenQuery,
  useLazyGetProjectByTokenQuery,
  useGetOpportunityByIdQuery,
  useGetMondayProjectByIdQuery,
  useGetMondayLineByBoardIdAndItemIdQuery,
  useLazyGetMondayLineByBoardIdAndItemIdQuery,
  useSearchProjectQuery,
  useLazySearchProjectQuery,
  useGetPluByProjectQuery,
  useGetPaginatedProjectQuery,
  useLazyGetPaginatedProjectQuery,
  useGetArchiveProjectByDateOfQuery,
  useGetArchiveProjectsCountQuery,
  useGetArchiveBdcQuery,
  useLazyGetArchiveBdcQuery,
  useGetArchiveBdcCountQuery,
  useLazyGetArchiveBdcCountQuery,
  useGetSortedClientQuery,
  useLazyGetSortedClientQuery,
  useTriggerPostInMondayMutation,
  useTriggerPrefillKizeoMutation,
  useTriggerSyncSignWithOdooMutation,
  useTriggerProject10PaymentMutation,
  useTriggerProjectFollowingInstructionsMutation,
  useTriggerProject50PaymentMutation,
  useSyncWithOdooMutation,
  useSyncWithMondayMutation,
  usePostCustomerR1NewMutation,
  usePostLostRestartMutation,
  usePatchProjectMutation,
  useDeleteUserRelatedMutation,
  usePatchNoShowMutation,
  usePatchReportMutation,
  usePatchNextMutation,
  usePatchCancelMutation,
  usePatchDeleteMutation,
  useGetPlanningByProjectIdQuery,
  useGetTechnicalVisitByProjectIdQuery,
  useGetTransportByProjectIdQuery,
  useGetIncidentByProjectIdQuery,
  useGetProductionByProjectIdQuery,
  useGetSAVByProjectIdQuery,
  useGetPluAnalyseByProjectIdQuery,
  usePatchPluAnalyseMutation,
  usePostDefaultOptionModelMutation,
  usePatchDefaultOptionModelMutation,
  useDeleteDefaultOptionModelMutation,
  // Quote
  useGetQuoteQuery,
  usePatchQuoteMutation,
  useDeleteQuoteMutation,
  // Task
  useGetAllTasksByUserQuery,
  useLazyGetAllTasksByUserQuery,
  useGetTaskTypeQuery,
  useGetTasksTypesByUserQuery,
  useGetTaskByProjectQuery,
  useLazyGetTaskByProjectQuery,
  useValidateTaskMutation,
  usePostTaskMutation,
  usePatchTaskMutation,
  usePatchWelcomeCallMutation,
  useNoShowWelcomeCallExMutation,
  useNoShowWelcomeCallMutation,
  usePatchTaskPieceComplementaireMutation,
  // Upsell
  useGetUpsellByIdQuery,
  useLazyGetUpsellByIdQuery,
  useDeleteUpsellMutation,
  useRefuseUpsellMutation,
  useGetUpsellByProjectQuery,
  useGetUpsellByConfigurationQuery,
  useGetUpsellByTokenQuery,
  useLazyGetUpsellByTokenQuery,
  useSendUpsellSignatureLinkQuery,
  usePatchUpsellMutation,
  usePatchSignatureUpsellMutation,
  useGetResultOfApplyUpsellQuery,
  useLazyGetResultOfApplyUpsellQuery,
  useGetArchiveUpsellsQuery,
  useLazyGetArchiveUpsellsQuery,
  useGetArchiveUpsellsCountQuery,
  useLazyGetArchiveUpsellsCountQuery,
  usePostConfigurationsUpsellMutation,
  // User
  useGetUserByIdQuery,
  useGetProjectByCustomerQuery,
  useGetTokenQuery,
  useForgotPasswordQuery,
  useLazyForgotPasswordQuery,
  useResetPasswordQuery,
  useLazyResetPasswordQuery,
  useGetProfileQuery,
  useSearchCustomersQuery,
  useLazySearchCustomersQuery,
  usePatchCustomerMutation,
  useGetEmployeesByTeamQuery,
  useLazyGetEmployeesByTeamQuery,
  usePostSendAccessQuery,
  useLazyPostSendAccessQuery,
  useGetAchivesClientsQuery,
  useLazyGetAchivesClientsQuery,
  useGetArchivesClientsCountQuery,
  useLazyGetArchivesClientsCountQuery,
  useGetCustomerTokenQuery,
  useLazyGetCustomerTokenQuery,
  useGetByProjectAndSlugQuery,
  useLazyGetByProjectAndSlugQuery,
  useDeleteTaskMutation,
  usePatchProjectConducteurTvxMutation,
  useGetRibsQuery,
  useChangeCurrentRibMutation,
} = apiSlice;
